import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard,
  FileText,
  Mail,
  Briefcase,
  ClipboardList,
  Footprints,
  DollarSign,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";
import { PenLine } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API } from "../../lib/operatorWalk";
import { AdminBadgesProvider } from "../../lib/adminBadges";
import Tm from "../ow/Tm";

const NAV = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard, end: true },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Contact Submissions", to: "/admin/contact", icon: Mail, badge: "contact" },
  { label: "Career Submissions", to: "/admin/careers", icon: Briefcase, badge: "careers" },
  { label: "Assessment Submissions", to: "/admin/assessments", icon: ClipboardList, badge: "assessments" },
  {
    label: "The Operator Walk",
    icon: Footprints,
    match: "/admin/operator-walk",
    children: [
      { label: "Overview", to: "/admin/operator-walk", end: true },
      { label: "Clients", to: "/admin/operator-walk/clients" },
      { label: "Reports", to: "/admin/operator-walk/reports" },
    ],
  },
  { label: "Pricing", to: "/admin/pricing", icon: DollarSign, match: "/admin/pricing" },
  { label: "Site Content", to: "/admin/content", icon: PenLine },
  { label: "Settings", to: "/admin/settings", icon: Settings },
];

function isActive(pathname, item) {
  if (item.children) return pathname.startsWith(item.match);
  if (item.end) return pathname === item.to;
  if (item.match) return pathname.startsWith(item.match);
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

function buildBreadcrumbs(pathname) {
  for (const item of NAV) {
    if (item.children) {
      for (const c of item.children) {
        const match = c.end ? pathname === c.to : pathname.startsWith(c.to);
        if (match) return [item.label, c.label];
      }
      if (pathname.startsWith(item.match)) return [item.label];
    } else if (isActive(pathname, item)) {
      return [item.label];
    }
  }
  return ["Dashboard"];
}

function Badge({ count }) {
  if (!count) return null;
  return (
    <span
      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold leading-none rounded-full bg-brand-300 text-brand-900"
      data-testid="admin-nav-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function SidebarNav({ pathname, onNavigate, counts }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="admin-sidebar-nav">
      {NAV.map((item) => {
        const active = isActive(pathname, item);
        const Icon = item.icon;
        if (item.children) {
          return (
            <div key={item.label} className="pt-1">
              <div
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm ${
                  active ? "text-white" : "text-brand-200"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="font-medium">
                  {item.label === "The Operator Walk" ? (
                    <>The Operator Walk<Tm /></>
                  ) : (
                    item.label
                  )}
                </span>
              </div>
              <div className="ml-4 pl-3 border-l border-brand-700 space-y-0.5 mt-0.5">
                {item.children.map((c) => {
                  const cActive = c.end ? pathname === c.to : pathname.startsWith(c.to);
                  return (
                    <Link
                      key={c.to}
                      to={c.to}
                      onClick={onNavigate}
                      className={`block px-3 py-2 text-sm rounded-sm transition-colors ${
                        cActive
                          ? "bg-brand-700 text-white"
                          : "text-brand-200 hover:bg-brand-800 hover:text-white"
                      }`}
                      data-testid={`admin-nav-${c.to.split("/").pop()}`}
                    >
                      {c.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        }
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
              active ? "bg-brand-700 text-white" : "text-brand-200 hover:bg-brand-800 hover:text-white"
            }`}
            data-testid={`admin-nav-${item.to === "/admin" ? "dashboard" : item.to.split("/").pop()}`}
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className="font-medium">{item.label}</span>
            {item.badge && <Badge count={counts[item.badge]} />}
          </Link>
        );
      })}
    </nav>
  );
}

export default function AdminLayout({ title, subtitle, actions, children }) {
  const { user, logout, authHeader } = usePortalAuth();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [counts, setCounts] = useState({ contact: 0, careers: 0, assessments: 0 });
  const crumbs = buildBreadcrumbs(pathname);

  const loadCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${OW_API}/admin/nav-counts`, { headers: authHeader });
      setCounts(res.data || {});
    } catch {
      // Badges are non-critical; silently skip on failure.
    }
  }, [authHeader]);

  // Refresh badge counts on each route change so marking items read updates them.
  useEffect(() => {
    loadCounts();
  }, [loadCounts, pathname]);

  // Optimistic adjustment so a badge ticks down the moment an item is marked read.
  const adjustBadge = useCallback((key, delta) => {
    setCounts((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));
  }, []);

  const SidebarInner = ({ onNavigate }) => (
    <>
      <div className="px-5 py-5 border-b border-brand-800">
        <Link to="/admin" onClick={onNavigate} className="flex items-center gap-3" data-testid="admin-sidebar-logo">
          <div className="w-9 h-9 bg-white text-brand-900 flex items-center justify-center font-serif text-lg leading-none">
            H
          </div>
          <div className="leading-tight">
            <div className="font-serif text-lg text-white">Admin</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-brand-300 -mt-0.5">
              Holder Hospitality
            </div>
          </div>
        </Link>
      </div>
      <SidebarNav pathname={pathname} onNavigate={onNavigate} counts={counts} />
      <div className="px-3 py-4 border-t border-brand-800">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="block px-3 py-2 text-xs text-brand-300 hover:text-white transition-colors"
          data-testid="admin-view-site-link"
        >
          View public site
        </a>
      </div>
    </>
  );

  return (
    <AdminBadgesProvider value={{ counts, adjustBadge }}>
    <div className="min-h-screen bg-brand-50 flex" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-brand-900 shrink-0 sticky top-0 h-screen">
        <SidebarInner onNavigate={undefined} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-brand-900/60" onClick={() => setDrawerOpen(false)} />
          <aside className="relative flex flex-col w-72 max-w-[80%] bg-brand-900 h-full">
            <button
              onClick={() => setDrawerOpen(false)}
              className="absolute top-4 right-4 text-brand-200 hover:text-white"
              data-testid="admin-drawer-close"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarInner onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-ink-line">
          <div className="px-4 lg:px-8 h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden text-brand-700 p-1 -ml-1"
                data-testid="admin-hamburger"
                aria-label="Open navigation"
              >
                <Menu className="w-6 h-6" />
              </button>
              <nav className="flex items-center gap-1.5 text-sm min-w-0" data-testid="admin-breadcrumbs">
                <span className="text-ink-muted hidden sm:inline">Admin</span>
                {crumbs.map((c, i) => (
                  <span key={i} className="flex items-center gap-1.5 min-w-0">
                    <ChevronRight className="w-3.5 h-3.5 text-ink-muted hidden sm:inline" />
                    <span className={`truncate ${i === crumbs.length - 1 ? "text-brand-700 font-medium" : "text-ink-muted"}`}>
                      {c}
                    </span>
                  </span>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right hidden sm:block leading-tight">
                <div className="text-sm text-brand-700 font-medium truncate max-w-[180px]">{user?.name || "Admin"}</div>
                <div className="text-[11px] text-ink-muted truncate max-w-[180px]">{user?.email}</div>
              </div>
              <button
                onClick={logout}
                className="inline-flex items-center gap-1.5 text-sm text-white bg-brand-700 hover:bg-brand-800 px-3.5 py-2 transition-colors"
                data-testid="admin-logout-btn"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page header + content */}
        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8 max-w-6xl w-full" data-testid="admin-main">
          {(title || actions) && (
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                {title && (
                  <h1 className="font-serif text-3xl lg:text-4xl text-brand-700 leading-tight" data-testid="admin-page-title">
                    {title}
                  </h1>
                )}
                {subtitle && <p className="mt-2 text-ink-soft text-sm lg:text-base">{subtitle}</p>}
              </div>
              {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
    </AdminBadgesProvider>
  );
}
